import { API_BASE, api } from "@/lib/apiClient";

/* ─────────────────────────── Typen ─────────────────────────── */

export type AgaStatus = "offen" | "in_bearbeitung" | "erledigt" | "abgelehnt";

export type AgaContactMode = "project" | "new";

export interface AgaPosition {
  id: number;
  sort_index: number | null;
  menge: number | null;
  einheit: string | null;
  text: string;
  notes: string | null;
  from_ai?: boolean;
  artikel_id?: number | null;
  hero_nr?: string | null;
  hero_unit?: string | null;
  ean?: string | null;
  source_ma_position_id?: number | null;
  created_at?: string | null;
}

export interface AgaPositionInput {
  sort_index?: number | null;
  menge: number | null;
  einheit: string | null;
  text: string;
  notes: string | null;
  from_ai?: boolean;
  artikel_id?: number | null;
  hero_nr?: string | null;
  hero_unit?: string | null;
  ean?: string | null;
  source_ma_position_id?: number | null;
}

export interface AgaAttachment {
  id: number;
  position_id?: number | null;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_by: string;
  uploaded_at: string;
  sharepoint_item_id?: string | null;
  sharepoint_web_url?: string | null;
}

export interface AgaListItem {
  id: number;
  aga_nummer: string;
  status: AgaStatus;
  auftrag_id: number | null;
  auftrag_nummer: string | null;
  auftrag_ort?: string | null;
  auftrag_beschreibung?: string | null;
  hero_project_id?: number | null;
  ordnerpfad?: string | null;
  sharepoint_path?: string | null;
  source_ma_id?: number | null;
  source_ma_nummer?: string | null;
  gd_angebot_id?: number | null;
  gd_angebot_nummer?: string | null;
  gd_angebot_status?: string | null;
  ansprechpartner_id: number | null;
  ansprechpartner_name?: string | null;
  contact_name: string | null;
  contact_company: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  contact_note: string | null;
  contact_display: string | null;
  notes: string | null;
  bearbeitungsnotizen?: string | null;
  bearbeitet_von: string | null;
  bearbeitet_von_name?: string | null;
  bearbeitet_at: string | null;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  attachment_count: number;
}

export interface AgaDetail extends AgaListItem {
  ansprechpartner_phone?: string | null;
  ansprechpartner_email?: string | null;
  ansprechpartner_company?: string | null;
  positionen: AgaPosition[];
  attachments: AgaAttachment[];
  takeover_from?: string | null;
  takeover_from_name?: string | null;
}

export interface AgaCreateInput {
  auftrag_id: number;
  contact_mode: AgaContactMode;
  ansprechpartner_id?: number | null;
  contact_name?: string | null;
  contact_company?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  contact_note?: string | null;
  notes?: string | null;
  positionen?: AgaPositionInput[];
}

export interface AgaUpdateInput {
  auftrag_id?: number | null;
  contact_mode?: AgaContactMode;
  ansprechpartner_id?: number | null;
  contact_name?: string | null;
  contact_company?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  contact_note?: string | null;
  notes?: string | null;
  bearbeitungsnotizen?: string | null;
}

export interface ProjectContact {
  auftrag_id: number;
  auftragsnummer?: string | null;
  hero_project_id?: number | null;
  ordnerpfad?: string | null;
  sharepoint_path?: string | null;
  ansprechpartner_id: number | null;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  company?: string | null;
}

/* ─────────────────────────── Liste / Detail ─────────────────────────── */

export interface ListAgaParams {
  status?: AgaStatus | "";
  q?: string;
  mineOnly?: boolean;
}

export async function listAngebotsanfragen(
  params: ListAgaParams = {},
): Promise<AgaListItem[]> {
  const usp = new URLSearchParams();
  if (params.status) usp.set("status", params.status);
  if (params.q) usp.set("q", params.q);
  if (params.mineOnly) usp.set("mine_only", "true");
  const qs = usp.toString();
  const path = qs ? `/angebotsanfragen?${qs}` : "/angebotsanfragen";
  return api.get<AgaListItem[]>(path);
}

export async function getAngebotsanfrage(id: number): Promise<AgaDetail> {
  return api.get<AgaDetail>(`/angebotsanfragen/${id}`);
}

export async function createAngebotsanfrage(
  payload: AgaCreateInput,
): Promise<AgaDetail> {
  return api.post<AgaDetail>("/angebotsanfragen", payload);
}

export async function patchAngebotsanfrage(
  id: number,
  payload: AgaUpdateInput,
): Promise<AgaDetail> {
  const res = await fetch(`${API_BASE}/angebotsanfragen/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return (await res.json()) as AgaDetail;
}

export async function deleteAngebotsanfrage(id: number): Promise<void> {
  await api.del(`/angebotsanfragen/${id}`);
}

export async function setAngebotsanfrageStatus(
  id: number,
  status: AgaStatus,
): Promise<AgaDetail> {
  return api.post<AgaDetail>(`/angebotsanfragen/${id}/status`, { status });
}

export async function claimAngebotsanfrage(id: number): Promise<AgaDetail> {
  return api.post<AgaDetail>(`/angebotsanfragen/${id}/claim`, {});
}

export interface AgaGdAngebotResult {
  ok: boolean;
  gd_angebot_id: number;
  already_exists: boolean;
  repaired?: boolean;
  aga_id: number;
  aga?: AgaDetail | null;
}

export async function createGdAngebotFromAga(id: number): Promise<AgaGdAngebotResult> {
  return api.post<AgaGdAngebotResult>(`/angebotsanfragen/${id}/dokumente/angebot`, {});
}

export interface AgaByGdAngebotRef {
  id: number;
  aga_nummer: string | null;
  status: AgaStatus;
  gd_angebot_id: number | null;
}

export async function getAngebotsanfrageByGdAngebot(
  gdId: number,
): Promise<AgaByGdAngebotRef> {
  return api.get<AgaByGdAngebotRef>(`/angebotsanfragen/by-gd-angebot/${gdId}`);
}

export async function fetchAngebotsanfragenOpenCount(): Promise<number> {
  const data = await api.get<{ count?: number }>("/angebotsanfragen/open-count");
  return Number(data?.count || 0);
}

export async function getProjectContact(
  auftragId: number,
): Promise<ProjectContact> {
  return api.get<ProjectContact>(`/angebotsanfragen/project-contact/${auftragId}`);
}

/* ─────────────────────────── Positionen ─────────────────────────── */

export async function addAgaPosition(
  agaId: number,
  payload: AgaPositionInput,
): Promise<{ id: number; aga: AgaDetail }> {
  return api.post<{ id: number; aga: AgaDetail }>(
    `/angebotsanfragen/${agaId}/positions`,
    payload,
  );
}

export async function patchAgaPosition(
  agaId: number,
  positionId: number,
  payload: Partial<AgaPositionInput>,
): Promise<AgaDetail> {
  return api.patch<AgaDetail>(
    `/angebotsanfragen/${agaId}/positions/${positionId}`,
    payload,
  );
}

export async function deleteAgaPosition(
  agaId: number,
  positionId: number,
): Promise<void> {
  await api.del(`/angebotsanfragen/${agaId}/positions/${positionId}`);
}

/* ─────────────────────────── Anhänge ─────────────────────────── */

export interface AddAttachmentResult {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
  position_id?: number | null;
  sharepoint_item_id?: string | null;
  sharepoint_web_url?: string | null;
}

export async function addAgaAttachment(
  agaId: number,
  file: File,
  positionId?: number | null,
): Promise<AddAttachmentResult> {
  const fd = new FormData();
  fd.append("file", file);
  if (positionId != null) {
    fd.append("position_id", String(positionId));
  }
  return api.postForm<AddAttachmentResult>(
    `/angebotsanfragen/${agaId}/attachments`,
    fd,
  );
}

export async function deleteAgaAttachment(
  agaId: number,
  attachmentId: number,
): Promise<void> {
  await api.del(`/angebotsanfragen/${agaId}/attachments/${attachmentId}`);
}

export function getAgaAttachmentUrl(agaId: number, attachmentId: number): string {
  const base = API_BASE || "";
  return `${base}/angebotsanfragen/${agaId}/attachments/${attachmentId}`;
}

/** Verkleinerte Anzeigequalität (schnell laden, weiterhin scharf). */
export function getAgaAttachmentThumbUrl(agaId: number, attachmentId: number): string {
  return `${getAgaAttachmentUrl(agaId, attachmentId)}/thumb`;
}

/* ─────────────────────────── Handy-Upload ─────────────────────────── */

export interface AgaMobileUploadQrResponse {
  url: string;
  expires_at: string | null;
  qr_data_uri: string;
}

export async function getAgaMobileUploadQr(
  agaId: number,
): Promise<AgaMobileUploadQrResponse> {
  return api.get<AgaMobileUploadQrResponse>(
    `/angebotsanfragen/${agaId}/mobile-upload-qr`,
  );
}
